import classes from './RigWell.module.scss';
import type {
  CatalogOperator,
  CatalogRig,
  CatalogTreeResponse,
  CatalogWell,
} from '@/models/models';
import { API_GET_CATALOG_TREE_ENDPOINT } from '@/api/config';

import {
  Accordion,
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { ContentSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton';
import IconAlertCircle from '@tabler/icons-react/IconAlertCircle';
import IconArrowRight from '@tabler/icons-react/IconArrowRight';
import IconBuilding from '@tabler/icons-react/IconBuilding';
import IconDroplet from '@tabler/icons-react/IconDroplet';
import IconSearch from '@tabler/icons-react/IconSearch';

import apiClient from '@/api/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';


function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (
      error as { response?: { data?: { detail?: unknown }; status?: number } }
    ).response;
    if (typeof response?.data?.detail === 'string') {
      return response.data.detail;
    }
    if (response?.status) {
      return `${fallback}: ${response.status}`;
    }
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

function matchesName(name: string, aliases: string[], query: string): boolean {
  return [name, ...aliases].some((value) => value.toLocaleLowerCase().includes(query));
}

function removeEmptyBranches(operators: CatalogOperator[]): CatalogOperator[] {
  return operators.flatMap((operator) => {
    const rigs = operator.rigs.filter((rig) => rig.wells.length > 0);
    return rigs.length > 0 ? [{ ...operator, rigs }] : [];
  });
}

function filterTree(operators: CatalogOperator[], query: string): CatalogOperator[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) {
    return operators;
  }

  return operators.flatMap((operator) => {
    const operatorMatches = matchesName(operator.name, operator.aliases, normalizedQuery);
    const rigs = operator.rigs.flatMap((rig) => {
      const rigMatches = matchesName(rig.name, rig.aliases, normalizedQuery);
      const wells = rig.wells.filter(
        (well) => rigMatches || operatorMatches || matchesName(well.name, well.aliases, normalizedQuery)
      );
      return wells.length > 0 ? [{ ...rig, wells }] : [];
    });
    return operatorMatches || rigs.length > 0 ? [{ ...operator, rigs }] : [];
  });
}

function RigDerrickIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      className={classes.rigIcon}
      fill="none"
      height={size}
      viewBox="0 0 32 32"
      width={size}
    >
      <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.1">
        <rect height="4" rx="0.8" width="12" x="10" y="2" />
        <path d="M12 6 7 30M20 6l5 24M8.7 22h14.6M9.8 17h12.4M11 11h10" />
        <path d="m11 11 11.2 6M21 11 9.8 17M9.8 17l13.5 5M22.2 17 8.7 22M8.7 22 25 30M23.3 22 7 30" />
        <rect height="4" rx="0.8" width="18" x="7" y="12" />
      </g>
    </svg>
  );
}

function AliasList({ aliases }: { aliases: string[] }) {
  const { t } = useLanguage();
  if (!aliases.length) {
    return null;
  }
  const aliasText = aliases.join(', ');
  return (
    <Text className={classes.aliases} c="dimmed" size="xs" title={aliasText} truncate>
      {t('rigWell.aliases')}: {aliasText}
    </Text>
  );
}

export default function RigWellTree() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [operators, setOperators] = useState<CatalogOperator[]>([]);
  const [openedOperators, setOpenedOperators] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const loadTree = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<CatalogTreeResponse>(
          API_GET_CATALOG_TREE_ENDPOINT
        );
        if (!active) return;
        const nextOperators = removeEmptyBranches(response.data.operators || []);
        setOperators(nextOperators);
      } catch (requestError) {
        if (active) {
          setOperators([]);
          setError(getErrorMessage(requestError, t('rigWell.loadError')));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadTree();
    return () => { active = false; };
  }, [t]);

  const filteredOperators = useMemo(
    () => filterTree(operators, query),
    [operators, query]
  );
  const isSearching = query.trim().length > 0;
  const visibleOpenedOperators = isSearching
    ? filteredOperators.map((operator) => String(operator.id))
    : openedOperators;

  useEffect(() => {
    if (!isSearching) {
      setOpenedOperators([]);
    }
  }, [isSearching]);

  const wellCount = useMemo(
    () => operators.reduce(
      (total, operator) => total + operator.rigs.reduce(
        (rigTotal, rig) => rigTotal + rig.wells.length,
        0
      ),
      0
    ),
    [operators]
  );

  const goToResults = useCallback((rig: CatalogRig, well: CatalogWell) => {
    const params = new URLSearchParams({ rig_name: rig.name, well_name: well.name });
    navigate(`/inference_results?${params.toString()}`);
  }, [navigate]);

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title={t('common.error')} color="red">
        {error}
      </Alert>
    );
  }

  return (
    <Stack gap={8} className={classes.treePage}>
      <Paper className={classes.toolbar}>
        <div>
          <Text fw={800} size="md" lh={1.2}>{t('rigWell.title')}</Text>
          <Text size="xs" c="dimmed">
            {t('rigWell.summary', { operators: operators.length, wells: wellCount })}
          </Text>
        </div>
        <TextInput
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder={t('rigWell.search')}
          leftSection={<IconSearch size={15} />}
          size="xs"
          className={classes.search}
        />
      </Paper>

      {loading ? (
        <Paper className={classes.loading}><ContentSkeleton /></Paper>
      ) : filteredOperators.length === 0 ? (
        <Paper className={classes.empty}>
          <Text c="dimmed">{t('rigWell.empty')}</Text>
        </Paper>
      ) : (
        <Accordion
          multiple
          value={visibleOpenedOperators}
          onChange={isSearching ? undefined : setOpenedOperators}
          variant="separated"
          className={classes.operatorList}
        >
          {filteredOperators.map((operator) => (
            <Accordion.Item value={String(operator.id)} key={operator.id} className={classes.operatorCard}>
              <Accordion.Control icon={<IconBuilding size={17} />}>
                <Group justify="space-between" gap={8} pr={4} wrap="nowrap">
                  <div className={classes.entityMeta}>
                    <Text fw={800} size="sm" lh={1.2} truncate>{operator.name}</Text>
                    <AliasList aliases={operator.aliases} />
                  </div>
                  <Badge color="green" variant="light" size="xs">
                    {t('rigWell.rigCount', { count: operator.rigs.length })}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap={5} className={classes.rigList}>
                  {operator.rigs.map((rig) => (
                    <details
                      key={`${rig.id}-${isSearching ? 'search' : 'browse'}`}
                      className={classes.rigBranch}
                      open={isSearching ? true : undefined}
                    >
                      <summary className={classes.rigHeader}>
                        <div className={classes.rigIdentity}>
                          <RigDerrickIcon />
                          <div className={classes.entityMeta}>
                            <Text fw={700} size="sm" lh={1.2} truncate>{rig.name}</Text>
                            <AliasList aliases={rig.aliases} />
                          </div>
                        </div>
                        <Badge color="teal" variant="light" size="xs">
                          {t('rigWell.wellCount', { count: rig.wells.length })}
                        </Badge>
                      </summary>
                      <Stack gap={4} className={classes.wellList}>
                        {rig.wells.map((well) => (
                          <div key={well.id} className={classes.wellRow}>
                            <Group gap={6} className={classes.wellIdentity} wrap="nowrap">
                              <IconDroplet size={14} />
                              <div className={classes.entityMeta}>
                                <Text size="xs" fw={650} lh={1.2} truncate>{well.name}</Text>
                                <AliasList aliases={well.aliases} />
                              </div>
                            </Group>
                            <Button
                              className={classes.resultsButton}
                              variant="light"
                              color="green"
                              size="xs"
                              rightSection={<IconArrowRight size={13} />}
                              onClick={() => goToResults(rig, well)}
                            >
                              {t('rigWell.goToResults')}
                            </Button>
                          </div>
                        ))}
                      </Stack>
                    </details>
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Stack>
  );
}
